/** `x-widget: "user-key"` — edits FareHarbor `user_key`.
 *
 * `user_key` is EITHER a single API user key (string — one company) OR an object
 * keyed by alias, whose value is a key string OR `{ key, companies: string[] }`.
 * Aliases are FareHarbor's own multi-company mechanism (independent of account
 * slugs). Backend: `user_key` string → used directly; dict → `user_key[alias]`
 * resolved to `.key` when it's an object (`fareharbor/integration.py`).
 *
 * Summary cell + dialog, mirroring ResourcesEditorDialog. The dialog has a
 * top-level mode (Single | By alias); alias rows switch between a plain key and
 * a key + companies list. Keys are secrets — the dialog is only reachable with a
 * revealed config (managers), so values are shown in the clear here.
 */
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Value model
// ---------------------------------------------------------------------------

export interface DetailedKey {
  key: string;
  companies: string[];
}
export type UserKeyValue = string | Record<string, string | DetailedKey>;

type AliasRow = { alias: string; detailed: boolean; key: string; companies: string[] };
type Model = { mode: 'single' | 'multi'; single: string; rows: AliasRow[] };

function isDetailed(v: unknown): v is Partial<DetailedKey> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function toModel(value: unknown): Model {
  if (typeof value === 'string') return { mode: 'single', single: value, rows: [] };
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const rows: AliasRow[] = Object.entries(value as Record<string, unknown>).map(([alias, v]) => {
      if (typeof v === 'string') return { alias, detailed: false, key: v, companies: [] };
      if (isDetailed(v)) {
        return {
          alias,
          detailed: true,
          key: typeof v.key === 'string' ? v.key : '',
          companies: Array.isArray(v.companies)
            ? v.companies.filter((c): c is string => typeof c === 'string')
            : [],
        };
      }
      return { alias, detailed: false, key: '', companies: [] };
    });
    return { mode: 'multi', single: '', rows };
  }
  return { mode: 'single', single: '', rows: [] };
}

function toValue(model: Model): UserKeyValue {
  if (model.mode === 'single') return model.single;
  const out: Record<string, string | DetailedKey> = {};
  for (const row of model.rows) {
    const a = row.alias.trim();
    if (!a) continue;
    out[a] = row.detailed ? { key: row.key, companies: row.companies } : row.key;
  }
  return out;
}

function summarize(value: unknown): string {
  if (typeof value === 'string') return value ? 'Single key' : 'No key set';
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const keys = Object.keys(value as object);
    if (keys.length === 0) return 'No keys';
    const preview = keys.slice(0, 3).join(', ');
    return keys.length > 3
      ? `${keys.length} aliases: ${preview}…`
      : `${keys.length} alias(es): ${preview}`;
  }
  return 'No key set';
}

// ---------------------------------------------------------------------------
// Companies chip editor (list of company aliases)
// ---------------------------------------------------------------------------

function CompaniesField({
  companies,
  onChange,
  readOnly,
}: {
  companies: string[];
  onChange: (next: string[]) => void;
  readOnly: boolean;
}) {
  const [input, setInput] = useState('');
  function add() {
    const v = input.trim();
    if (!v || companies.includes(v)) {
      setInput('');
      return;
    }
    onChange([...companies, v]);
    setInput('');
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {companies.map((c, i) => (
        <Chip
          key={i}
          label={c}
          size="small"
          onDelete={readOnly ? undefined : () => onChange(companies.filter((_, j) => j !== i))}
        />
      ))}
      {!readOnly && (
        <TextField
          size="small"
          variant="standard"
          value={input}
          placeholder="add company…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          sx={{ '& input': { py: 0.25, px: 0.75, width: 120, fontSize: 12 } }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface DialogProps {
  open: boolean;
  value: unknown;
  readOnly: boolean;
  onClose: () => void;
  onDone: (next: UserKeyValue) => void;
}

function UserKeyDialog({ open, value, readOnly, onClose, onDone }: DialogProps) {
  const [model, setModel] = useState<Model>(() => toModel(value));

  useEffect(() => {
    if (open) setModel(toModel(value));
  }, [open, value]);

  function patchRow(index: number, patch: Partial<AliasRow>) {
    setModel((m) => ({ ...m, rows: m.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>FareHarbor user key(s)</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          size="small"
          label="Mode"
          value={model.mode}
          disabled={readOnly}
          onChange={(e) => setModel((m) => ({ ...m, mode: e.target.value as Model['mode'] }))}
          sx={{ mb: 2, minWidth: 260 }}
        >
          <MenuItem value="single">Single company (one key)</MenuItem>
          <MenuItem value="multi">Multiple companies (keyed by alias)</MenuItem>
        </TextField>

        {model.mode === 'single' ? (
          <TextField
            fullWidth
            size="small"
            label="API user key"
            value={model.single}
            disabled={readOnly}
            onChange={(e) => setModel((m) => ({ ...m, single: e.target.value }))}
            sx={{ '& input': { fontFamily: 'monospace' } }}
          />
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Each alias maps to an API user key. Use “Key + companies” to attach the FareHarbor
              company aliases that key is valid for.
            </Typography>
            <Stack spacing={1.5}>
              {model.rows.length === 0 && (
                <Typography variant="body2" color="text.disabled">
                  No aliases.
                </Typography>
              )}
              {model.rows.map((row, index) => (
                <Box
                  key={index}
                  sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Alias"
                        size="small"
                        fullWidth
                        value={row.alias}
                        disabled={readOnly}
                        onChange={(e) => patchRow(index, { alias: e.target.value })}
                        sx={{ '& input': { fontFamily: 'monospace' } }}
                      />
                    </Grid>
                    <Grid item xs={8} sm={6}>
                      <TextField
                        select
                        label="Value"
                        size="small"
                        fullWidth
                        value={row.detailed ? 'detailed' : 'simple'}
                        disabled={readOnly}
                        onChange={(e) =>
                          patchRow(index, { detailed: e.target.value === 'detailed' })
                        }
                      >
                        <MenuItem value="simple">Key only</MenuItem>
                        <MenuItem value="detailed">Key + companies</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={4} sm={2} sx={{ textAlign: 'right' }}>
                      {!readOnly && (
                        <Tooltip title="Remove alias" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setModel((m) => ({
                                ...m,
                                rows: m.rows.filter((_, i) => i !== index),
                              }))
                            }
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="API user key"
                        size="small"
                        fullWidth
                        value={row.key}
                        disabled={readOnly}
                        onChange={(e) => patchRow(index, { key: e.target.value })}
                        sx={{ '& input': { fontFamily: 'monospace' } }}
                      />
                    </Grid>
                    {row.detailed && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Companies (FareHarbor company aliases)
                        </Typography>
                        <CompaniesField
                          companies={row.companies}
                          onChange={(companies) => patchRow(index, { companies })}
                          readOnly={readOnly}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </Stack>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setModel((m) => ({
                    ...m,
                    rows: [...m.rows, { alias: '', detailed: false, key: '', companies: [] }],
                  }))
                }
                sx={{ mt: 1.5, textTransform: 'none' }}
              >
                Add alias
              </Button>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onDone(toValue(model))} disabled={readOnly}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cell (rendered by the dispatcher)
// ---------------------------------------------------------------------------

interface CellProps {
  value: unknown;
  onChange: (next: UserKeyValue) => void;
  readOnly?: boolean;
}

export function UserKeyCell({ value, onChange, readOnly = false }: CellProps) {
  const [open, setOpen] = useState(false);
  const current = useRef<unknown>(value);
  current.current = value;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
        {summarize(current.current)}
      </Typography>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {readOnly ? 'View keys…' : 'Edit keys…'}
      </Button>
      <UserKeyDialog
        open={open}
        value={current.current}
        readOnly={readOnly}
        onClose={() => setOpen(false)}
        onDone={(next) => {
          onChange(next);
          setOpen(false);
        }}
      />
    </Box>
  );
}
